export type LeaveDocumentDifference = {
  id: number;
  key: string;
  kind: string;
  status: string;
  status_display: string;
  transaction_date: string | null;
  mutation_label: string;
  leave_type_name: string;
  document_value: { minutes?: number };
  app_value: { minutes?: number };
  difference: { minutes?: number };
  document_minutes: number;
  app_minutes: number;
  difference_minutes: number;
  matched: boolean;
  decision_reason: string;
  decided_at: string | null;
};

export type LeaveDocumentAnalysis = {
  id: number;
  version: number;
  normalized_data: {
    document_type?: string;
    period?: { start: string | null; end: string | null };
    employee?: { name?: string | null; employer_name?: string | null };
    leave_entries?: unknown[];
    balances?: unknown[];
    transport_balances?: unknown[];
    corrections?: unknown[];
    unknown_terms?: Array<{ raw_term?: string; value?: string }>;
    uncertain_values?: Array<{
      kind?: string;
      index?: number | null;
      extracted_value?: number;
      confidence?: number;
      source?: { page?: number; raw_text?: string };
    }>;
  };
  warnings: string[];
  differences: LeaveDocumentDifference[];
};

export type LeaveDocumentCounts = {
  total: number;
  open: number;
  mismatched: number;
};

export type LeaveDocument = {
  id: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  period_start: string | null;
  period_end: string | null;
  employer_name: string;
  status: string;
  status_display: string;
  analysis_version: number;
  error_message: string;
  difference_counts: LeaveDocumentCounts;
  duplicate_document_ids: number[];
  same_period_document_ids: number[];
  analyses: LeaveDocumentAnalysis[];
};

export type LeaveDocumentListRow = Omit<
  LeaveDocument,
  "analyses" | "duplicate_document_ids" | "same_period_document_ids"
> & { fully_matched: boolean };

// Matchcriterium: welk verloftype hoort bij een term uit een werkgeversdocument.
export type EmployerLeaveTypeMapping = {
  id: number;
  employer_name: string;
  raw_term: string;
  leave_type: number | null;
  leave_type_name: string;
  ignored: boolean;
  active: boolean;
  source: string;
  created_at: string;
  last_used_at: string | null;
};

export const DECISION_OPTIONS = [
  { value: "app_correct", label: "Mijn verlofkaart klopt" },
  { value: "document_correct_applied", label: "Werkgever klopt, overgenomen" },
  { value: "ignored", label: "Genegeerd" },
] as const;

const count = (rows?: unknown[]) => rows?.length ?? 0;

export function leaveEntryCount(analysis: LeaveDocumentAnalysis) {
  return count(analysis.normalized_data.leave_entries);
}

export function balanceRowCount(analysis: LeaveDocumentAnalysis) {
  return (
    count(analysis.normalized_data.balances) +
    count(analysis.normalized_data.transport_balances) +
    count(analysis.normalized_data.corrections)
  );
}

// Een analyse zonder regels, onbekende termen of verschillen: niets om aan de
// gebruiker voor te leggen, en dus iets om expliciet te benoemen.
export function hasNothingToCompare(analysis: LeaveDocumentAnalysis) {
  return (
    leaveEntryCount(analysis) === 0 &&
    balanceRowCount(analysis) === 0 &&
    count(analysis.normalized_data.unknown_terms) === 0 &&
    analysis.differences.length === 0
  );
}

export function periodLabel(row: {
  period_start: string | null;
  period_end: string | null;
}) {
  if (!row.period_start && !row.period_end) return "Periode onbekend";
  const format = (value: string) => value.split("-").reverse().join("-");
  if (row.period_start && row.period_end) {
    return row.period_start === row.period_end
      ? format(row.period_start)
      : `${format(row.period_start)} t/m ${format(row.period_end)}`;
  }
  return format((row.period_start ?? row.period_end) as string);
}
